// @flow

import * as React from 'react';
import {useState} from 'react';
import Box from 'sail/Box';
import {ButtonLink} from 'sail/Button';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
} from 'sail/DataTable';
import {InlineLink} from 'sail/InlineLink';
import {BodyMono, Subheading} from 'sail/Text';
import {Badge} from 'docs/components';
import type {
  ConditionDocumentation,
  ConditionArgumentDocumentation,
} from 'docs/types';
import './RegisteredConditionsTable.scss';

type ConditionOwnerProps = {|
  owner: ?string,
|};

const ConditionOwner = ({owner}: ConditionOwnerProps) => {
  if (!owner) {
    return <Badge color="yellow" icon="warning" label="Unknown" />;
  }
  return <Badge color="gray" label={owner} />;
};

type ConditionNameProps = {|
  name: string,
  filename: ?string,
|};

const ConditionName = ({name, filename}: ConditionNameProps) => {
  if (!filename) {
    return name;
  }

  const liveGrepUrl = `https://livegrep.corp.stripe.com/view/stripe-internal/pay-server${filename}`;
  return (
    <InlineLink href={liveGrepUrl} target="_blank">
      {name}
    </InlineLink>
  );
};

const NoArgumentMessage = () => (
  <React.Fragment>
    <DataTableRow background="offset">
      <DataTableCell />
      <DataTableCell colSpan={3}>No arguments required</DataTableCell>
    </DataTableRow>
  </React.Fragment>
);

type ConditionArgumentProps = {|
  argument: ConditionArgumentDocumentation,
|};

const ConditionArgument = ({argument}: ConditionArgumentProps) => (
  <React.Fragment>
    <DataTableRow background="offset">
      <DataTableCell />
      <DataTableCell>
        <Subheading>Argument</Subheading>
      </DataTableCell>
      <DataTableCell colSpan={2}>
        <Subheading>Sorbet Type</Subheading>
      </DataTableCell>
    </DataTableRow>
    <DataTableRow background="offset">
      <DataTableCell />
      <DataTableCell>
        <BodyMono>{argument.name}</BodyMono>
      </DataTableCell>
      <DataTableCell colSpan={2}>
        <BodyMono>{argument.type}</BodyMono>
      </DataTableCell>
    </DataTableRow>
  </React.Fragment>
);

type ConditionRowGroupProps = {|
  name: string,
  condition: ConditionDocumentation,
|};

const ConditionRowGroup = ({name, condition}: ConditionRowGroupProps) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    setExpanded((current) => !current);
  };

  let argument;
  if (expanded) {
    if (condition.argument) {
      argument = <ConditionArgument argument={condition.argument} />;
    } else {
      argument = <NoArgumentMessage />;
    }
  }

  return (
    <React.Fragment>
      <DataTableRow className="RegisteredConditionsTable-ConditionRowGroup">
        <DataTableCell>
          <ButtonLink
            color="gray"
            icon={expanded ? 'chevronUp' : 'chevronDown'}
            label="Toggle properties"
            hideLabel
            onClick={handleToggle}
          />
        </DataTableCell>
        <DataTableCell width={200}>
          <BodyMono>
            <ConditionName name={name} filename={condition.source_filename} />
          </BodyMono>
        </DataTableCell>
        <DataTableCell width={150}>
          <ConditionOwner owner={condition.owner} />
        </DataTableCell>
        <DataTableCell>{condition.description}</DataTableCell>
      </DataTableRow>
      {argument}
    </React.Fragment>
  );
};

type RegisteredConditionsTableProps = {|
  conditions: {[name: string]: ConditionDocumentation},
|};

export const RegisteredConditionsTable = ({
  conditions,
}: RegisteredConditionsTableProps) => (
  <Box padding={{top: 12}}>
    <DataTable className="RegisteredConditionsTable" size="medium">
      <DataTableHead
        columns={[
          {},
          {label: 'Name'},
          {label: 'Owner'},
          {label: 'Description'},
        ]}
      />
      <DataTableBody>
        {Object.keys(conditions).map((name) => (
          <ConditionRowGroup
            key={name}
            name={name}
            condition={conditions[name]}
          />
        ))}
      </DataTableBody>
    </DataTable>
  </Box>
);
